import { EFinancingProgram, EProvince } from '@if/core/types'
import { MultiTranslatedError } from '@if/mui'
import { BaseWorksheetFormConfig, validateApr, WorksheetError } from '@src/components/WorksheetForm'
import { useComputePersonalLoanFunding } from '@src/data/api/personal-loan-worksheet/personal-loan-worksheet-api'
import { useProvinceConfigurations } from '@src/data/selectors/app-config-selectors'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CalculatorValues } from '../calculatorSchema'

export interface UseCalculatorComputeArgs {
  config: BaseWorksheetFormConfig | null
  financingProgramId: EFinancingProgram | null
  hasCoApplicant: boolean
  stateIso: EProvince | null
  creditApplicationId: string | null
  merchantId?: string
  interestRate: number
  insuranceCompanyId: string | null
}

export interface CalculatorComputeResult {
  info: FundingComputedDto
  values: CalculatorValues
}

export interface UseCalculatorComputeReturn {
  compute: (values: CalculatorValues) => Promise<void>
  result: CalculatorComputeResult | null
  isComputing: boolean
}

export const useCalculatorCompute = ({
  config,
  financingProgramId,
  hasCoApplicant,
  stateIso,
  creditApplicationId,
  merchantId,
  interestRate,
  insuranceCompanyId,
}: UseCalculatorComputeArgs): UseCalculatorComputeReturn => {
  const [computeFunding, isComputing] = useComputePersonalLoanFunding()
  const provinceConfigs = useProvinceConfigurations()
  const [result, setResult] = useState<CalculatorComputeResult | null>(null)

  const abortRef = useRef<AbortController | null>(null)
  useEffect(() => () => abortRef.current?.abort(), [])

  const compute = useCallback(
    async (data: CalculatorValues) => {
      if (!config || !financingProgramId || !stateIso) return
      if (!data.firstPaymentDate || data.term === null) return
      if (data.amountRequested === null || data.paymentPlanId === null || !data.activationDate) return

      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      const dto: FundingInfoDto = {
        financingProgramId,
        amountRequested: data.amountRequested,
        includeInsurance: data.includeProtection,
        insuranceCompanyId,
        paymentPlanId: data.paymentPlanId,
        paymentFrequency: data.paymentFrequency,
        term: data.term,
        interestRate,
        hasCoApplicant,
        deliveryOn: data.activationDate,
        firstPaymentOn: data.firstPaymentDate.toString(),
        requestId: nanoid(),
        stateIso,
        creditApplicationId: creditApplicationId ?? '',
        merchantId: merchantId ?? null,
      }

      try {
        const info = await computeFunding(dto)
        if (controller.signal.aborted) return

        const errors: WorksheetError[] = []
        if (config.effectiveRateThreshold > 0) {
          const aprError = validateApr({
            computedInfo: info,
            stateIso: config.stateIso,
            effectiveRateThreshold: config.effectiveRateThreshold,
            provinceConfigs,
          })
          if (aprError) errors.push(aprError)
        }
        if (errors.length > 0) {
          throw new MultiTranslatedError(errors.map((e) => ({ key: e.messageKey, options: e.messageArgs })))
        }
        setResult({ info, values: data })
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setResult(null)
        throw err
      }
    },
    [
      config,
      financingProgramId,
      hasCoApplicant,
      stateIso,
      creditApplicationId,
      merchantId,
      interestRate,
      insuranceCompanyId,
      computeFunding,
      provinceConfigs,
    ],
  )

  return { compute, result, isComputing }
}
